import '@create-figma-plugin/ui/css/base.css';
import '@create-figma-plugin/ui/css/fonts.css';
import '@create-figma-plugin/ui/css/theme.css';
import { render } from 'preact';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { installImageRpc } from './image-rpc';
import { apiConfig } from './config/apiConfig';
import { installChunkRecovery } from './utils/lazy-with-recovery';
import {
  configureErrorReporting,
  flushErrors,
  installGlobalErrorHandlers,
  type ErrorBatch,
} from './helpers/logger';

// Answer the tofig engine's image-channel RPC (getImageResolution / atob) from
// the browser, where images can actually be measured/decoded. Install before
// render so no early request from the sandbox is missed.
installImageRpc();

/**
 * Error reporting, pointed at this app's own API.
 *
 * The endpoint is injected rather than imported by the logger, because `apiConfig` imports the
 * logger — wiring it the other way round would be a cycle. It is also why the old code hardcoded
 * a URL, and hardcoded the wrong one.
 *
 * `keepalive` matters more than it looks: the plugin iframe is torn down the moment the user
 * closes the panel, and an ordinary fetch in flight dies with it — which is exactly when the
 * report explaining why they closed it is being sent.
 */
configureErrorReporting({
  source: 'ui',
  buildId: __BUILD_ID__,
  transport: async (batch: ErrorBatch) => {
    const response = await fetch(`${apiConfig.baseUrl}/log-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(batch),
      keepalive: true,
    });
    if (!response.ok) throw new Error(`log-errors responded ${response.status}`);
  },
});

// Uncaught exceptions and unhandled rejections — the errors nobody wrote a catch for, which were
// the ones never being collected.
installGlobalErrorHandlers(window);

/**
 * A module PRELOAD failing does not come through the import() rejection that
 * `withChunkRecovery` wraps — Vite fires `vite:preloadError` on window instead. Same cause (a
 * deploy replaced the hashed filenames mid-session), same repair, same loop guard.
 */
installChunkRecovery(window);

// The panel closing is a normal exit, not an edge case; `pagehide` is the last point at which
// anything can still be sent.
window.addEventListener('pagehide', () => {
  void flushErrors();
});

// The plugin UI is hosted (a nested cross-origin iframe), so Figma does not
// inject its theme CSS variables here. `@create-figma-plugin/ui`'s theme.css
// defines them under a `.figma-dark` / `.figma-light` class — apply the dark
// theme to the body so the components (and the tab styles) render correctly.
document.body.classList.add('figma-dark');

// Server state is owned by TanStack Query. Production-grade defaults: retry
// transient failures with exponential backoff, keep reads briefly fresh, and
// don't refetch on window focus (a plugin panel focuses constantly).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    },
    mutations: { retry: 1 },
  },
});

render(
  <QueryClientProvider client={queryClient}>
    <App />
  </QueryClientProvider>,
  document.getElementById('root')!,
);
